import { getCurrentUser } from '@/lib/auth-utils'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { User } from '@/models'
import { neon } from '@/services/neon'

function requireAdmin() {
  const user = getCurrentUser()
  if (!user || user.role !== ROLE_ADMIN) {
    throw new Error('You do not have permission to perform this action.')
  }
}

type ProfileUserRow = {
  id: string
  name?: string | null
  email?: string | null
  role?: string | null
  is_banned?: boolean | null
  is_active?: boolean | null
  is_approved?: boolean | null
  company_id?: string | null
  username?: string | null
  last_login?: string | null
  created_at?: string | null
  updated_at?: string | null
  subscription_status?: string | null
  subscription_end_date?: string | null
  referral_code?: string | null
}

type CompanyRow = {
  id: string
  name?: string | null
}

export type UpdateUserProfileInput = Pick<
  User,
  'role' | 'is_banned' | 'is_active' | 'company_id' | 'is_approved'
>

const USERS_SELECT_EXTENDED =
  'id,name,email,role,is_banned,is_active,is_approved,company_id,username,last_login,created_at,updated_at,subscription_status,subscription_end_date,referral_code'
const USERS_SELECT_CORE =
  'id,role,is_banned,is_active,is_approved,company_id,username,last_login,created_at,updated_at,subscription_status,subscription_end_date,referral_code'

const isMissingColumnError = (message?: string | null) =>
  Boolean(message && /column\s+users\.(name|email)\s+does not exist/i.test(message))

export async function fetchUsers(): Promise<User[]> {
  requireAdmin()
  let usersRes = await neon.from('users').select(USERS_SELECT_EXTENDED)

  if (usersRes.error && isMissingColumnError(usersRes.error.message)) {
    usersRes = await neon.from('users').select(USERS_SELECT_CORE)
  }

  if (usersRes.error) {
    throw new Error(usersRes.error.message)
  }

  const profileUsers = (usersRes.data as ProfileUserRow[]) || []

  const hasMissingCompanyNames = profileUsers.some((profile) => profile.company_id)

  let companyMap = new Map<string, string>()
  if (hasMissingCompanyNames) {
    const companiesRes = await neon.from('companies').select('id,name')
    if (!companiesRes.error) {
      companyMap = new Map(
        ((companiesRes.data as CompanyRow[]) || []).map((company) => [
          company.id,
          company.name || '',
        ]),
      )
    }
  }

  return profileUsers.map((profile) => {
    const companyNameFromMap = profile.company_id
      ? companyMap.get(profile.company_id) || undefined
      : undefined

    return {
      id: profile.id,
      name: profile.name || undefined,
      email: profile.email || undefined,
      username: profile.username || undefined,
      last_login: profile.last_login || undefined,
      created_at: profile.created_at || undefined,
      updated_at: profile.updated_at || undefined,
      role: profile.role || 'user',
      is_banned: profile.is_banned ?? false,
      is_active: profile.is_active ?? true,
      is_approved: profile.is_approved ?? false,
      company_id: profile.company_id || undefined,
      company_name: companyNameFromMap,
      subscription_status: profile.subscription_status || undefined,
      subscription_end_date: profile.subscription_end_date || undefined,
    }
  })
}

export async function fetchUserById(id: string): Promise<User | null> {
  const currentUser = getCurrentUser()
  if (currentUser?.id !== id) {
    requireAdmin()
  }
  let profileRes = await neon.from('users').select(USERS_SELECT_EXTENDED).eq('id', id).single()

  if (profileRes.error && isMissingColumnError(profileRes.error.message)) {
    profileRes = await neon.from('users').select(USERS_SELECT_CORE).eq('id', id).single()
  }

  if (profileRes.error) {
    const isNotFound =
      profileRes.error.code === 'PGRST116' || profileRes.error.details?.includes('0 rows')
    if (isNotFound) {
      return null
    }
    throw new Error(profileRes.error.message)
  }

  const profileUser = (profileRes.data as ProfileUserRow | null) || null
  if (!profileUser) {
    return null
  }

  let companyName: string | undefined
  if (profileUser.company_id) {
    const companiesRes = await neon
      .from('companies')
      .select('id,name')
      .eq('id', profileUser.company_id as string)
      .single()

    if (!companiesRes.error) {
      companyName =
        ((companiesRes.data as CompanyRow | null)?.name as string | undefined) || undefined
    }
  }

  return {
    id: profileUser.id,
    name: profileUser.name || undefined,
    email: profileUser.email || undefined,
    username: profileUser.username || undefined,
    last_login: profileUser.last_login || undefined,
    created_at: profileUser.created_at || undefined,
    updated_at: profileUser.updated_at || undefined,
    role: profileUser?.role || 'user',
    is_banned: profileUser?.is_banned ?? false,
    is_active: profileUser?.is_active ?? true,
    is_approved: profileUser?.is_approved ?? false,
    company_id: profileUser?.company_id || undefined,
    company_name: companyName,
    subscription_status: (profileUser as any).subscription_status || undefined,
    subscription_end_date: (profileUser as any).subscription_end_date || undefined,
  }
}

export async function updateUserProfile(id: string, user: UpdateUserProfileInput): Promise<void> {
  requireAdmin()
  const payload: Record<string, any> = {
    role: user.role,
    is_banned: user.is_banned,
    is_active: user.is_active,
    company_id: user.company_id ?? null,
  }
  if (user.is_approved !== undefined) payload.is_approved = user.is_approved

  const { error } = await neon.from('users').update(payload).eq('id', id)

  if (error) {
    throw new Error(error.message)
  }
}

export async function banUser(id: string): Promise<void> {
  requireAdmin()
  const { error } = await neon.from('users').update({ is_banned: true }).eq('id', id)
  if (error) {
    throw new Error(error.message)
  }
}

export async function unbanUser(id: string): Promise<void> {
  requireAdmin()
  const { error } = await neon.from('users').update({ is_banned: false }).eq('id', id)
  if (error) {
    throw new Error(error.message)
  }
}

export async function disableUser(id: string): Promise<void> {
  requireAdmin()
  const { error } = await neon.from('users').update({ is_active: false }).eq('id', id)
  if (error) {
    throw new Error(error.message)
  }
}

export async function enableUser(id: string): Promise<void> {
  requireAdmin()
  const { error } = await neon.from('users').update({ is_active: true }).eq('id', id)
  if (error) {
    throw new Error(error.message)
  }
}

export async function updateUserAvatar(id: string, avatarKey: string): Promise<void> {
  const currentUser = getCurrentUser()
  if (!currentUser?.id) throw new Error('User not authenticated.')
  if (currentUser.role !== ROLE_ADMIN && currentUser.id !== id) {
    throw new Error('You can only update your own avatar.')
  }

  const { error } = await neon.from('users').upsert({ id, avatar: avatarKey }, { onConflict: 'id' })

  if (error) {
    throw new Error(error.message)
  }
}

export async function updateUserName(id: string, name: string, username: string): Promise<void> {
  const currentUser = getCurrentUser()
  if (currentUser?.id !== id) {
    throw new Error('You can only update your own profile.')
  }
  const { error } = await neon.from('users').update({ name, username }).eq('id', id)
  if (error) {
    throw new Error(error.message)
  }
}

export async function updateLastLogin(id: string): Promise<void> {
  const { error } = await neon
    .from('users')
    .update({ last_login: new Date().toISOString() })
    .eq('id', id)
  if (error) {
    throw new Error(error.message)
  }
}

export async function approveUser(id: string): Promise<void> {
  requireAdmin()
  const { error } = await neon.from('users').update({ is_approved: true }).eq('id', id)
  if (error) {
    throw new Error(error.message)
  }
}

export async function fetchPendingUsers(): Promise<User[]> {
  requireAdmin()
  const usersRes = await neon.from('users').select(USERS_SELECT_EXTENDED).eq('is_approved', false)

  if (usersRes.error) throw new Error(usersRes.error.message)

  const profileUsers = (usersRes.data as ProfileUserRow[]) || []
  if (profileUsers.length === 0) return []

  const hasMissingCompanyNames = profileUsers.some((profile) => profile.company_id)
  let companyMap = new Map<string, string>()
  if (hasMissingCompanyNames) {
    const companiesRes = await neon.from('companies').select('id,name')
    if (!companiesRes.error) {
      companyMap = new Map(
        ((companiesRes.data as CompanyRow[]) || []).map((company) => [
          company.id,
          company.name || '',
        ]),
      )
    }
  }

  return profileUsers.map((profile) => {
    const companyNameFromMap = profile.company_id
      ? companyMap.get(profile.company_id) || undefined
      : undefined
    return {
      id: profile.id,
      name: profile.name || undefined,
      email: profile.email || undefined,
      username: profile.username || undefined,
      last_login: profile.last_login || undefined,
      created_at: profile.created_at || undefined,
      updated_at: profile.updated_at || undefined,
      role: profile.role || 'user',
      is_banned: profile.is_banned ?? false,
      is_active: profile.is_active ?? true,
      is_approved: profile.is_approved ?? false,
      company_id: profile.company_id || undefined,
      company_name: companyNameFromMap,
      subscription_status: profile.subscription_status || undefined,
      subscription_end_date: profile.subscription_end_date || undefined,
    }
  })
}
